import { useEffect, useRef, useState } from 'react'
import {
  UserCheck,
  BarChart3,
  Brain,
  Database,
  Mail,
  ChevronRight,
  Menu,
  X,
  Puzzle,
  Network,
  TrendingUp,
  Eye,
  Calculator,
  Sparkles,
  Play,
  ClipboardCheck,
  FileText,
  BookOpen,
  Gauge,
  Target,
  ArrowRight,
  FlaskConical,
  FolderTree,
  LineChart,
  FileBarChart,
  GraduationCap,
  CheckCircle2,
} from 'lucide-react'
import InquiryModal from '@/components/InquiryModal'
import { initReferrer, getReferrer } from '@/utils/referrer'

// ---------------------------------------------------------------------------
// Scroll-triggered fade-in
// ---------------------------------------------------------------------------
function FadeIn({
  children,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode
  delay?: number
  className?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={`transition-all duration-800 ease-out ${className}`}
      style={{
        transitionDelay: `${delay}ms`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
      }}
    >
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Animated stat bar (animates width on visibility)
// ---------------------------------------------------------------------------
function AnimatedBar({ pct, primary }: { pct: number; primary: boolean }) {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setWidth(pct), 200)
          observer.disconnect()
        }
      },
      { threshold: 0.5 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [pct])

  return (
    <div ref={ref} className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-1000 ease-out ${
          primary
            ? 'bg-gradient-to-r from-[#004098] to-[#00a0e9]'
            : 'bg-gray-300'
        }`}
        style={{ width: `${width}%` }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Module image slider
// ---------------------------------------------------------------------------
const MODULE_SLIDES = [
  '/landing/design/module-1-card.svg',
  '/landing/design/module-2-card.svg',
  '/landing/design/module-3-card.svg',
  '/landing/design/module-4-card.svg',
]

function ModuleSlider() {
  const [current, setCurrent] = useState(0)

  const prev = () => setCurrent((c) => (c === 0 ? MODULE_SLIDES.length - 1 : c - 1))
  const next = () => setCurrent((c) => (c === MODULE_SLIDES.length - 1 ? 0 : c + 1))

  return (
    <div className="mt-12 sm:mt-16 max-w-md mx-auto">
      <div className="relative">
        <div className="overflow-hidden rounded-2xl">
          <img
            src={MODULE_SLIDES[current]}
            alt={`교육 모듈 ${current + 1}`}
            className="w-full transition-opacity duration-300"
          />
        </div>

        {/* Prev / Next buttons */}
        <button
          onClick={prev}
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
          aria-label="이전"
        >
          <ChevronRight className="w-5 h-5 text-gray-600 rotate-180" />
        </button>
        <button
          onClick={next}
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
          aria-label="다음"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-6">
        {MODULE_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
              i === current ? 'bg-[#004098] w-6' : 'bg-gray-300'
            }`}
            aria-label={`슬라이드 ${i + 1}`}
          />
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [inquiryType, setInquiryType] = useState<'DEMO' | 'INQUIRY' | null>(null)
  const [hasReferrer, setHasReferrer] = useState(false)

  useEffect(() => {
    initReferrer()
    setHasReferrer(!!getReferrer())
    const timer = setTimeout(() => setMounted(true), 80)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollTo = (id: string) => {
    setMobileMenuOpen(false)
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  const NAV_ITEMS = [
    { id: 'transformation', label: 'AX 시대' },
    { id: 'aiq-definition', label: 'AIQ란' },
    { id: 'learning-system', label: '학습 시스템' },
    { id: 'modules', label: '커리큘럼' },
    { id: 'aiq-test', label: '평가 시스템' },
  ]

  return (
    <div className="min-h-screen bg-white text-[#161519] overflow-x-hidden antialiased">
      {/* ── Sticky Navbar ── */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/70 backdrop-blur-xl border-b border-white/20 shadow-[0_4px_30px_rgba(0,0,0,0.08)]'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <button onClick={() => scrollTo('hero')} className="flex items-center gap-2 cursor-pointer">
            <img
              src="/landing/design/aiqlab-logo.svg"
              alt="AIQ Lab"
              className={`h-[25px] transition-all duration-300 ${scrolled ? '' : 'brightness-0 invert'}`}
            />
          </button>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className={`px-4 py-2 text-[15px] font-medium rounded-lg transition-all cursor-pointer ${
                  scrolled
                    ? 'text-gray-500 hover:text-[#004098] hover:bg-[#004098]/5'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:block">
            <button
              onClick={() => setInquiryType('INQUIRY')}
              className={`inline-flex items-center gap-2 text-[13px] font-semibold px-5 py-2.5 rounded-xl transition-all duration-200 hover:-translate-y-0.5 cursor-pointer ${
                scrolled
                  ? 'bg-gradient-to-r from-[#004098] to-[#00a0e9] text-white hover:shadow-lg hover:shadow-[#004098]/25'
                  : 'bg-white/20 backdrop-blur-sm text-white border border-white/30 hover:bg-white/30'
              }`}
            >
              도입 문의
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              scrolled ? 'hover:bg-gray-100' : 'text-white hover:bg-white/10'
            }`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 ${mobileMenuOpen ? 'max-h-80' : 'max-h-0'}`}>
          <div className="bg-white border-t border-gray-100 px-6 py-4 space-y-1">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className="block w-full text-left text-sm text-gray-600 hover:text-[#004098] hover:bg-[#004098]/5 py-2.5 px-3 rounded-lg transition-colors"
              >
                {item.label}
              </button>
            ))}
            <div className="pt-2">
              <button
                onClick={() => setInquiryType('INQUIRY')}
                className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#004098] to-[#00a0e9] text-white cursor-pointer"
              >
                도입 문의
                <Mail className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ══════════════════════════════════════════════════════════════════
          Section 1: Hero
          ══════════════════════════════════════════════════════════════════ */}
      <section id="hero" className="relative pt-16 overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0">
          <video
            className="w-full h-full object-cover"
            autoPlay
            loop
            muted
            playsInline
          >
            <source
              src="https://d2ql3aoebfyj3b.cloudfront.net/video/about-main.mp4"
              type="video/mp4"
            />
          </video>
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div
          className={`relative max-w-7xl mx-auto px-6 lg:px-8 pt-24 sm:pt-32 pb-16 text-center transition-all duration-1000 ease-out ${
            mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Title */}
          <h1 className="font-a2z text-2xl sm:text-3xl lg:text-4xl font-extrabold !leading-[1.4] tracking-tight">
            <span className="text-white">
              AI 시대를 위한
            </span>
            <br />
            <span className="text-white">새로운 인재 양성 솔루션</span>
          </h1>

          {/* AIQ Logo/Slogan */}
          <div className="mt-8 sm:mt-10 flex flex-col items-center">
            <img
              src="/landing/design/aiqlab-slogan-white.svg"
              alt="AIQ - Artificial Intelligence Quotient"
              className="h-24 sm:h-32 lg:h-40"
            />
          </div>

          {/* 4-card grid */}
          <div className="mt-16 sm:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-6xl mx-auto">
            {[
              {
                icon: UserCheck,
                title: '새로운 인재 기준',
                desc: 'AI 시대에 필요한 핵심 인재를 재정의하는 새로운 기준입니다.',
                accent: '#004098',
              },
              {
                icon: BarChart3,
                title: '새로운 HR 솔루션',
                desc: 'AI 시대에 필요한 역량을 양성하기 위해\n개발된 혁신적 교육과 평가 솔루션입니다.',
                accent: '#00a0e9',
              },
              {
                icon: Brain,
                title: '3가지 핵심 역량',
                desc: '수학을 활용한 3가지 핵심 역량 교육으로 AIQ를 성장시킵니다.',
                accent: '#0070d0',
              },
              {
                icon: Database,
                title: '데이터 및 분석 지원',
                desc: '기업의 인재 채용과 육성 등 HR 전반에\n활용 가능한 데이터와 분석을 지원합니다.',
                accent: '#004098',
              },
            ].map((card, i) => (
              <FadeIn key={i} delay={i * 80}>
                <div className="group bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6 sm:p-7 text-left hover:bg-white/15 hover:-translate-y-1 transition-all duration-300 h-full">
                  <div className="flex items-center gap-3.5 mb-3.5">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110 bg-white/15"
                    >
                      <card.icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-bold text-[15px] text-white">
                      {card.title}
                    </h3>
                  </div>
                  <p className="text-[13px] text-white/70 leading-relaxed whitespace-pre-line">{card.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>

      </section>

      {/* ══════════════════════════════════════════════════════════════════
          Section 2: AI Transformation
          ══════════════════════════════════════════════════════════════════ */}
      <section id="transformation" className="relative bg-gradient-to-b from-[#e8f2fa] to-[#f0f7ff]">
        {/* Subtle side accent */}
        <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-[#00a0e9]/30 via-[#004098]/10 to-transparent hidden lg:block" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-20">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="font-a2z text-2xl sm:text-3xl lg:text-[36px] font-extrabold !leading-[1.4]">
                인류 역사상 가장 거대한 변화의 물결
              </h2>
              <p className="font-a2z mt-4 text-4xl sm:text-5xl lg:text-[56px] font-black tracking-tight bg-gradient-to-r from-[#004098] to-[#00a0e9] bg-clip-text text-transparent !leading-[1.4]">
                AI Transformation
              </p>
              <p className="font-a2z mt-6 sm:mt-8 text-2xl sm:text-3xl text-[#161519] font-extrabold">
                우리 조직은 준비되어 있습니까?
              </p>
            </div>
          </FadeIn>

          {/* 3 change cards */}
          <div className="mt-12 sm:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 max-w-4xl mx-auto">
            {[
              {
                icon: Database,
                line1: '일의 정의가',
                highlight: '근본부터 재편',
                line2: '됩니다.',
                gradient: 'from-[#004098] to-[#0060c0]',
              },
              {
                icon: Network,
                line1: '협업의 대상이',
                highlight: 'AI로 확장',
                line2: '됩니다.',
                gradient: 'from-[#0060c0] to-[#00a0e9]',
              },
              {
                icon: Brain,
                line1: '사고방식의 혁신이',
                highlight: '인간의 생존 조건',
                line2: '입니다.',
                gradient: 'from-[#00a0e9] to-[#40c0f0]',
              },
            ].map((card, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div className="relative bg-white rounded-2xl p-7 sm:p-8 text-center shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,64,152,0.08)] transition-all duration-300 min-h-[200px] flex flex-col items-center justify-center overflow-hidden group">
                  {/* Top accent bar */}
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${card.gradient} opacity-60 group-hover:opacity-100 transition-opacity`} />
                  <div
                    className="w-20 h-20 rounded-2xl flex items-center justify-center mb-5"
                    style={{ background: `linear-gradient(135deg, ${i === 0 ? '#004098' : i === 1 ? '#0060c0' : '#00a0e9'}20, ${i === 0 ? '#0060c0' : i === 1 ? '#00a0e9' : '#40c0f0'}30)` }}
                  >
                    <card.icon className="w-10 h-10" style={{ color: i === 0 ? '#004098' : i === 1 ? '#0060c0' : '#00a0e9' }} strokeWidth={1.5} />
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {card.line1}
                  </p>
                  <p className={`font-extrabold text-lg bg-gradient-to-r ${card.gradient} bg-clip-text text-transparent`}>
                    {card.highlight}
                  </p>
                  <p className="text-sm text-gray-600">{card.line2}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* HR Stats */}
          <FadeIn>
            <div className="mt-20 sm:mt-24 text-center">
              <p className="font-a2z text-xl sm:text-2xl text-[#161519] font-extrabold">기업의 미래를 위해</p>
              <h3 className="font-a2z text-2xl sm:text-3xl lg:text-4xl font-extrabold mt-2">
                가장 시급한 과제, <span className="bg-gradient-to-r from-[#004098] to-[#00a0e9] bg-clip-text text-transparent">HR</span>
              </h3>
            </div>
          </FadeIn>

          <FadeIn>
            <div className="mt-10 sm:mt-12 max-w-3xl mx-auto bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-7 sm:p-9">
              <div className="flex items-baseline gap-2 mb-8">
                <p className="text-lg font-bold text-[#161519]">
                  기업의 변화를 막는 장애 요인
                </p>
                <p className="text-sm text-gray-400">- 글로벌 1000대 기업</p>
              </div>

              {[
                { rank: '1위', label: '노동 시장의 요구와 직원 역량의 격차', pct: 63, primary: true },
                { rank: '2위', label: '변화를 거부하는 조직 문화', pct: 46, primary: false },
                { rank: '3위', label: '시대착오적 규제와 제도', pct: 39, primary: false },
              ].map((item, i) => (
                <div key={i} className="mb-6 last:mb-0">
                  <p className={`text-base mb-2 ${item.primary ? 'text-[#004098] font-bold' : 'text-gray-500'}`}>
                    {item.rank} - {item.label}
                  </p>
                  <div className="flex items-center gap-4">
                    <AnimatedBar pct={item.pct} primary={item.primary} />
                    <span className={`${item.primary ? 'text-2xl text-[#004098]' : 'text-xl text-gray-300'} font-extrabold min-w-[56px] text-right tabular-nums`}>
                      {item.pct}%
                    </span>
                  </div>
                </div>
              ))}

              <p className="text-[11px] text-gray-300 mt-6 pt-4 border-t border-gray-100">
                * The Future of Jobs Report 2025 | World Economic Forum
              </p>
            </div>
          </FadeIn>

          {/* CTA */}
          <FadeIn>
            <div className="mt-16 sm:mt-20 text-center">
              <h3 className="font-a2z text-3xl sm:text-4xl font-black bg-gradient-to-r from-[#004098] to-[#00a0e9] bg-clip-text text-transparent">
                지금 바로 준비하세요!
              </h3>
              <p className="mt-3 text-lg sm:text-xl text-gray-600 font-medium">
                아니면, 내일의 격차는 따라잡을 수 없습니다.
              </p>
            </div>
          </FadeIn>
        </div>

      </section>

      {/* ══════════════════════════════════════════════════════════════════
          Section 3: AIQ 정의
          ══════════════════════════════════════════════════════════════════ */}
      <section id="aiq-definition" className="bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-20">
          {/* Header */}
          <FadeIn>
            <p className="text-sm font-semibold text-[#00a0e9] uppercase tracking-widest mb-4">What is AIQ</p>
            <h2 className="font-a2z text-2xl sm:text-3xl lg:text-[40px] font-extrabold !leading-[1.4]">
              <span className="text-[#00a0e9]">AIQ는 메타적 문제해결력을 정의하는</span>
              <br />
              <span className="text-[#161519]">새로운 기준입니다</span>
            </h2>
            <p className="mt-4 text-[#004098] font-semibold text-base sm:text-lg">
              2030 핵심 역량을 선정하는 유일한 솔루션
            </p>
          </FadeIn>

          {/* 2030 Diagram */}
          <FadeIn>
            <div className="mt-10 sm:mt-14">
              <img
                src="/landing/design/2030-diagram-pc.svg"
                alt="2030 메타적 문제해결력 다이어그램"
                className="w-full hidden sm:block"
              />
              <img
                src="/landing/design/2030-diagram-mobile.svg"
                alt="2030 메타적 문제해결력 다이어그램"
                className="w-full sm:hidden"
              />
              <div className="mt-8 text-left text-sm text-gray-500 leading-relaxed">
                <p className="font-semibold text-gray-600">※ '메타적 문제해결력'이란?</p>
                <p className="mt-1">- 고도화된 AI의 기술력을 이용해 각 종 문제를 해결하는 인간의 새로운 능력입니다.</p>
                <p>- 기계(AI)가 대체할 수 없는 인간의 고유한 능력을 의미합니다.</p>
              </div>
            </div>
          </FadeIn>

          {/* 3 Capabilities */}
          <FadeIn>
            <div className="mt-14 sm:mt-20">
              <p className="text-sm font-semibold text-[#00a0e9] uppercase tracking-widest mb-4">Core Competencies</p>
              <h3 className="font-a2z text-2xl sm:text-3xl lg:text-[36px] font-extrabold !leading-[1.4]">
                <span className="text-[#00a0e9]">AIQ를 수학적 사고 기반의</span>
                <br />
                <span className="text-[#161519]">3가지 역량으로 구체화합니다</span>
              </h3>
              <p className="mt-4 text-[#004098] font-semibold text-base sm:text-lg">
                무시·관계·변화 기반의 핵심 역량 집중 교육
              </p>
            </div>
          </FadeIn>

          <div className="mt-10 sm:mt-14 grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6">
            {[
              {
                iconSrc: '/landing/design/core-competencies-abstractiging.svg',
                title: 'Abstracting',
                desc: '문제의 본질만 남기는 힘',
                detail: '0을 이해하고 문제를 재정의하는 능력',
                gradient: 'from-[#004098] to-[#0060c0]',
                iconBg: '#004098',
              },
              {
                iconSrc: '/landing/design/core-competencies-relating.svg',
                title: 'Relating',
                desc: '세상을 연결망(관계)으로 읽는 힘',
                detail: '비율, 인과, 구조를 통합하는 능력',
                gradient: 'from-[#0060c0] to-[#00a0e9]',
                iconBg: '#0070d0',
              },
              {
                iconSrc: '/landing/design/core-competencies-change.svg',
                title: 'Change',
                desc: '변화를 발견하고 미래를 예측하는 힘',
                detail: '패턴과 기울기를 읽는 능력',
                gradient: 'from-[#00a0e9] to-[#40c0f0]',
                iconBg: '#00a0e9',
              },
            ].map((cap, i) => (
              <FadeIn key={i} delay={i * 120}>
                <div className="group relative bg-white rounded-2xl border border-gray-100 p-7 sm:p-8 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgba(0,64,152,0.12)] hover:-translate-y-1 transition-all duration-300 h-full overflow-hidden text-center">
                  {/* Top gradient accent */}
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${cap.gradient} opacity-40 group-hover:opacity-100 transition-opacity`} />
                  <div className="flex justify-center mb-6">
                    <img src={cap.iconSrc} alt={cap.title} className="w-20 h-20 sm:w-24 sm:h-24 transition-transform duration-300 group-hover:scale-110" />
                  </div>
                  <h4 className={`text-xl font-extrabold bg-gradient-to-r ${cap.gradient} bg-clip-text text-transparent mb-3`}>
                    {cap.title}
                  </h4>
                  <p className="text-sm font-semibold text-gray-800 mb-1">{cap.desc}</p>
                  <p className="text-[13px] text-gray-400 leading-relaxed">{cap.detail}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>

      </section>

      {/* ══════════════════════════════════════════════════════════════════
          Section 4: 학습 시스템
          ══════════════════════════════════════════════════════════════════ */}
      <section id="learning-system" className="bg-[#e8f4fd]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-20">
          <FadeIn>
            <p className="text-sm font-semibold text-[#00a0e9] uppercase tracking-widest mb-4">Learning System</p>
            <h2 className="font-a2z text-2xl sm:text-3xl lg:text-[40px] font-extrabold !leading-[1.4]">
              <span className="text-[#00a0e9]">직관과 논리의 융합을 통해</span>
              <br />
              <span className="text-[#161519]">AIQ를 체계적으로 성장시킵니다</span>
            </h2>
            <p className="mt-4 text-[#004098] font-semibold text-base sm:text-lg">
              특허받은 학습 시스템을 활용한 사고와 발상의 전환
            </p>
          </FadeIn>

          {/* 3 learning approaches */}
          <div className="mt-10 sm:mt-14 grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6">
            {[
              {
                title: '시각화',
                desc: '고도로 추상화된 개념과 원리를\n특허받은 시각화 기술로 만든\n애니메이션 영상으로 학습합니다.',
                images: ['/landing/design/learning-system-1-1.png', '/landing/design/learning-system-1-2.png'],
              },
              {
                title: '논리적 추론',
                desc: '의미 없는 기계적 반복을 제거하고\n기존의 문제해결 방식을 대체하는\n새로운 통찰을 제공합니다.',
                images: ['/landing/design/learning-system-2-1.png', '/landing/design/learning-system-2-2.png'],
              },
              {
                title: '직관',
                desc: '시각화와 논리적 추론을 통해 얻은\n수학적 사고력이 직관적인\n메타적 문제해결로 발현됩니다.',
                images: ['/landing/design/learning-system-3-1.png', '/landing/design/learning-system-3-2.png'],
              },
            ].map((item, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div className="bg-white rounded-2xl p-5 sm:p-6 h-full">
                  <h4 className="font-extrabold text-[#004098] text-xl text-center mb-3">{item.title}</h4>
                  <p className="text-[13px] text-gray-500 leading-relaxed text-center whitespace-pre-line mb-5">{item.desc}</p>
                  <div className="space-y-3">
                    {item.images.map((src, j) => (
                      <img key={j} src={src} alt={`${item.title} 예시 ${j + 1}`} className="w-full rounded-xl" />
                    ))}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* Micro Learning */}
          <div className="mt-14 sm:mt-20">
            <FadeIn>
              <p className="text-sm font-semibold text-[#00a0e9] uppercase tracking-widest mb-4">Micro Learning</p>
              <h3 className="font-a2z text-2xl sm:text-3xl lg:text-[36px] font-extrabold !leading-[1.4]">
                <span className="text-[#00a0e9]">마이크로 러닝으로</span>
                <br />
                <span className="text-[#161519]">임직원 누구나 부담 없이 시작할 수 있습니다</span>
              </h3>
              <p className="mt-4 text-[#004098] font-semibold text-base sm:text-lg">
                실무 중심의 AIQ 교육 프로세스
              </p>
            </FadeIn>

            <div className="mt-10 sm:mt-14 grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6">
              {[
                {
                  step: '1',
                  title: '온라인 VOD 시청',
                  desc: '독창적인 애니메이션 영상을 통해\n발상과 사고를 전환시키는 시청각 학습',
                  image: '/landing/design/micro-learning-1.png',
                },
                {
                  step: '2',
                  title: '과제 수행',
                  desc: '실무 기반의 문제를 대상으로 수학적\n사고력을 동원한 문제 해결 과정 진단',
                  image: '/landing/design/micro-learning-2.svg',
                },
                {
                  step: '3',
                  title: '최종 리포트 작성',
                  desc: '다양한 실무에 즉시 적용 가능한\n아이디어 기반의 주·객관식 리포트 작성',
                  image: '/landing/design/micro-learning-3.svg',
                },
              ].map((item, i) => (
                <FadeIn key={i} delay={i * 120}>
                  <div className="bg-white rounded-2xl p-5 sm:p-6 h-full text-center">
                    <div className="flex justify-center mb-3">
                      <div className="w-9 h-9 rounded-full bg-[#00a0e9] flex items-center justify-center text-white font-bold text-sm">
                        {item.step}
                      </div>
                    </div>
                    <h4 className="font-extrabold text-[#004098] text-xl mb-2">{item.title}</h4>
                    <p className="text-[13px] text-gray-500 leading-relaxed whitespace-pre-line mb-5">{item.desc}</p>
                    <img src={item.image} alt={item.title} className="w-full rounded-xl" />
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>

      </section>

      {/* ══════════════════════════════════════════════════════════════════
          Section 5: 모듈 소개
          ══════════════════════════════════════════════════════════════════ */}
      <section id="modules" className="bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-20">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-sm font-semibold text-[#00a0e9] uppercase tracking-widest mb-4">Education Modules</p>
              <h2 className="font-a2z text-2xl sm:text-3xl lg:text-[40px] font-extrabold !leading-[1.4]">
                <span className="text-[#00a0e9]">공통 모듈과 직급별 모듈</span>로
                <br />
                임직원의 AIQ를 성장시키세요
              </h2>
            </div>
          </FadeIn>

          {/* Image Slider */}
          <FadeIn>
            <ModuleSlider />
          </FadeIn>

          {/* Original cards (commented out)
          <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            {[
              { badge: '공통', icon: BookOpen, badgeColor: '#004098', title: '메타적 문제해결의 첫걸음', subtitle: '메타 코어', items: ['메타적 사고를 빠르게 체험하고 공감을 이끌어 낼 수 있는 실전 프로그램', '메타적 사고 역량의 필요성과 3가지 핵심 역량의 효과를 직접 체험합니다.'], tags: ['수학적 사고력', '메타인지', '문제해결'], duration: '약 30분' },
              { badge: '사원급', icon: BarChart3, badgeColor: '#00a0e9', title: '입머리 좋은 사원의', subtitle: '남다른 데이터 리터러시', items: ['데이터 리터러시를 높여 효율적 분석과 업무성과를 향상시킵니다.', '데이터 기반 의사결정과 보고서 작성 스킬을 동시에 향상시킵니다.'], tags: ['데이터 분석', '의사결정', '보고서'], duration: '약 45분' },
              { badge: '팀장급', icon: Gauge, badgeColor: '#006dc0', title: '눈 보다 먹 아는 리더의', subtitle: '빠른 수리감', items: ['모든 채널에서 숫자의 의미를 빠르게 파악하고 경영의 전략 수립에 활용합니다.', '숫자 정보를 종합하여 다양한 관점의 전략을 수립합니다.'], tags: ['수리적 비즈', '전략 수립', '데이터 판단'], duration: '약 60분' },
              { badge: '임원급', icon: Target, badgeColor: '#003070', title: '청라인으로 내다보는 리더의', subtitle: '빠른 예측력', items: ['미래 핵심 트렌드를 읽고 전략에 반영합니다.', '변화와 위기 속 3가지 핵심 요인을 분석하고 경영에 활용합니다.'], tags: ['미래 시나리오', '인사이트', '변화의 법칙'], duration: '약 60분' },
            ].map((mod, i) => ( ... ))}
          </div>
          */}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          Section 6: AIQ Test
          ══════════════════════════════════════════════════════════════════ */}
      <section id="aiq-test" className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#004098] via-[#0060c0] to-[#00a0e9]" />
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 40%)',
        }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-20 text-white">
          <FadeIn>
            <div className="text-center mb-14 sm:mb-20">
              <p className="text-sm font-semibold text-white/50 uppercase tracking-widest mb-4">Assessment & Analytics</p>
              <h2 className="font-a2z text-3xl sm:text-4xl lg:text-5xl font-extrabold !leading-[1.4]">
                AIQ Test로 교육 성과를
                <br />
                진단하고 관리합니다
              </h2>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {/* Left: AIQ Test Process */}
            <FadeIn className="h-full">
              <div className="bg-white/[0.08] backdrop-blur-md rounded-3xl border border-white/[0.12] p-7 sm:p-9 h-full">
                <h3 className="text-lg font-bold mb-8 flex items-center gap-2">
                  <FlaskConical className="w-5 h-5 text-[#80d0ff]" />
                  <span>
                    <span className="text-[#80d0ff]">AIQ Test</span> 프로세스
                  </span>
                </h3>

                <div className="space-y-1">
                  {[
                    {
                      icon: GraduationCap,
                      step: 'Test',
                      desc: '논리, 직관, 추론 등 영역별 문제를 시행',
                      tags: ['Logic', 'Intuition', 'Reasoning'],
                    },
                    {
                      icon: FolderTree,
                      step: 'Classification',
                      desc: '3가지 핵심 역량별 데이터 분류',
                      tags: ['Abstracting', 'Relating', 'Change'],
                    },
                    {
                      icon: LineChart,
                      step: 'Analysis',
                      desc: 'Key Entity 데이터의 AIQ Formula 대입',
                      tags: ['Response Time', 'Accuracy Rate', 'Time Improvement'],
                    },
                    {
                      icon: FileBarChart,
                      step: 'Reporting',
                      desc: 'AIQ / 직무역량 대시보드 및 리포트 제공',
                      tags: ['AIQ 지수 및 레벨', '역량 분석', '직무역량 관계'],
                    },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4 group">
                      <div className="flex flex-col items-center">
                        <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0 group-hover:bg-white/20 transition-colors">
                          <item.icon className="w-5 h-5 text-[#80d0ff]" />
                        </div>
                        {i < 3 && (
                          <div className="w-px flex-1 bg-gradient-to-b from-white/20 to-white/5 my-1" />
                        )}
                      </div>
                      <div className="pb-6">
                        <h4 className="font-bold text-white text-[15px]">{item.step}</h4>
                        <p className="text-[13px] text-white/50 mt-1">{item.desc}</p>
                        <div className="flex gap-1.5 flex-wrap mt-2.5">
                          {item.tags.map((tag, j) => (
                            <span key={j} className="text-[11px] px-2.5 py-1 bg-white/[0.08] rounded-lg text-white/60 font-medium">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>

            {/* Right column */}
            <div className="h-full">
              {/* AIQ Level */}
              <FadeIn delay={100} className="h-full">
                <div className="bg-white/[0.08] backdrop-blur-md rounded-3xl border border-white/[0.12] p-7 sm:p-9 h-full">
                  <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-[#80d0ff]" />
                    <span>
                      <span className="text-[#80d0ff]">AIQ</span> 레벨 체계
                    </span>
                  </h3>

                  <div className="space-y-3">
                    {[
                      {
                        level: 'META',
                        desc: '메타적 문제해결력 보유',
                        detail: '주어진 문제의 핵심을 파악해 새롭게 정의하는 데서 출발',
                        opacity: 'bg-white/[0.12]',
                      },
                      {
                        level: 'APPLIED',
                        desc: '전술적 문제해결력 보유',
                        detail: '주어진 문제의 유형을 유추하는 데서 출발',
                        opacity: 'bg-white/[0.07]',
                      },
                      {
                        level: 'SKILLED',
                        desc: '기계적 문제해결력 보유',
                        detail: '주어진 문제에 맞는 공식과 요령을 찾는 데서 출발',
                        opacity: 'bg-white/[0.04]',
                      },
                    ].map((item, i) => (
                      <div key={i} className={`${item.opacity} rounded-xl p-4 border border-white/[0.06] hover:border-white/[0.15] transition-colors`}>
                        <div className="flex items-center gap-3 mb-1.5">
                          <span className="text-sm font-extrabold text-white tracking-wide">{item.level}</span>
                          <div className="flex gap-1.5">
                            <span className="text-[10px] px-2 py-0.5 bg-white/15 rounded text-white/80 font-medium">High</span>
                            <span className="text-[10px] px-2 py-0.5 bg-white/[0.06] rounded text-white/40 font-medium">Low</span>
                          </div>
                        </div>
                        <p className="text-[13px] font-semibold text-white/80">{item.desc}</p>
                        <p className="text-[12px] text-white/35 mt-1">{item.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </FadeIn>

            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          Section: AIQ 솔루션 프로세스
          ══════════════════════════════════════════════════════════════════ */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-24 sm:pt-32 pb-16 sm:pb-20">
          <FadeIn>
            <div className="text-center mb-16 sm:mb-20">
              <p className="text-sm font-semibold text-[#00a0e9] uppercase tracking-widest mb-4">Solution Process</p>
              <h2 className="font-a2z text-2xl sm:text-3xl lg:text-[40px] font-extrabold !leading-[1.4]">
                <span className="text-[#00a0e9]">AIQ 솔루션</span> 프로세스
              </h2>
            </div>
          </FadeIn>

          <FadeIn>
            <div className="relative">
              {/* Connector line behind circles (desktop) */}
              <div className="hidden lg:block absolute top-6 left-[calc(100%/12)] right-[calc(100%/12)] h-px bg-gradient-to-r from-[#004098]/30 via-[#00a0e9]/30 to-[#004098]/30" />

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 relative">
                {[
                  { title: '교육 신청', desc: '임직원별 교육 과정 선택' },
                  { title: 'Pre-AIQ Test', desc: '교육 수강 전 메타적 문제해결력 측정' },
                  { title: '교육 수강', desc: '각 과정별 마이크로 러닝 시작' },
                  { title: '교육 이수', desc: '각 과정별 기준에 따른 교육 이수' },
                  { title: 'Post-AIQ Test', desc: '교육 수강 후 메타적 문제해결력 측정' },
                  { title: '리포트 데이터 제공', desc: '수강 결과 및 AIQ Test 리포팅' },
                ].map((item, i) => (
                  <div key={i} className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#004098] to-[#00a0e9] flex items-center justify-center text-white font-bold text-lg mb-3 shadow-lg shadow-[#004098]/20 relative z-10">
                      {i + 1}
                    </div>
                    <h4 className="text-sm font-bold text-[#004098] mb-1">{item.title}</h4>
                    <p className="text-[12px] text-gray-400 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          CTA Banner
          ══════════════════════════════════════════════════════════════════ */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <div className="relative bg-gradient-to-br from-[#f0f7ff] to-[#e8f2fd] rounded-3xl p-10 sm:p-16 text-center overflow-hidden">
              {/* Decorative circles */}
              <div className="absolute -top-20 -left-20 w-60 h-60 rounded-full bg-[#004098]/[0.04]" />
              <div className="absolute -bottom-16 -right-16 w-48 h-48 rounded-full bg-[#00a0e9]/[0.06]" />

              <div className="relative">
                <h2 className="font-a2z text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#161519]">
                  <span className="bg-gradient-to-r from-[#004098] to-[#00a0e9] bg-clip-text text-transparent">AIQ 솔루션</span>, 지금 경험하세요
                </h2>
                <p className="mt-4 text-base text-gray-500 max-w-lg mx-auto leading-relaxed">
                  AX 시대에 맞는 HR의 새로운 기준을 경험하세요.
                  <br className="hidden sm:block" />
                  전문 컨설턴트가 맞춤 데모를 제공합니다.
                </p>

                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    onClick={() => setInquiryType('DEMO')}
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-[#004098] to-[#00a0e9] text-white font-semibold text-base hover:shadow-xl hover:shadow-[#004098]/20 hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-200 cursor-pointer"
                  >
                    무료 데모 신청
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setInquiryType('INQUIRY')}
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border-2 border-gray-200 text-gray-600 font-semibold text-base hover:border-[#004098]/30 hover:text-[#004098] transition-all duration-200 cursor-pointer"
                  >
                    문의하기
                  </button>
                </div>

                {/* Email chip */}
                {!hasReferrer && (
                  <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-100 text-sm text-gray-400 shadow-sm">
                    <Mail className="w-4 h-4 text-[#00a0e9]" />
                    aiqedu@quebon.com
                  </div>
                )}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          Footer
          ══════════════════════════════════════════════════════════════════ */}
      <footer className="bg-[#f8f9fb] border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <img src="/landing/design/aiqlab-logo.svg" alt="AIQ Lab" className="h-5 opacity-40" />
              <span className="text-[13px] text-gray-400">
                &copy; {new Date().getFullYear()} EQUALKEY. All rights reserved.
              </span>
            </div>
            {!hasReferrer && (
              <a
                href="mailto:aiqedu@quebon.com"
                className="text-[13px] text-gray-400 hover:text-[#004098] transition-colors flex items-center gap-1.5"
              >
                <Mail className="w-3.5 h-3.5" />
                aiqedu@quebon.com
              </a>
            )}
          </div>
        </div>
      </footer>

      {/* Inquiry Modal */}
      {inquiryType && (
        <InquiryModal type={inquiryType} onClose={() => setInquiryType(null)} />
      )}
    </div>
  )
}
